import React, { Component } from "react";
import PropTypes from "prop-types";

import { isDefined, isNotDefined, noop } from "../utils";

import {
  getValueFromOverride,
  terminate,
  saveNodeType,
  isHoverForInteractiveType,
} from "./utils";

import EachRegressionLine from "./wrapper/EachRegressionLine";
import MouseLocationIndicator from "./components/MouseLocationIndicator";
import HoverTextNearMouse from "./components/HoverTextNearMouse";

class RegressionLine extends Component {
  constructor(props) {
    super(props);

    this.handleStart = this.handleStart.bind(this);
    this.handleEnd = this.handleEnd.bind(this);
    this.handleDrawLine = this.handleDrawLine.bind(this);
    this.handleDragLine = this.handleDragLine.bind(this);
    this.handleDragLineComplete = this.handleDragLineComplete.bind(this);

    this.terminate = terminate.bind(this);
    this.saveNodeType = saveNodeType.bind(this);

    this.getSelectionState =
      isHoverForInteractiveType("regressions").bind(this);

    this.nodes = [];
    this.state = {};
  }
  handleDragLine(index, newXYValue) {
    this.setState({
      override: {
        index,
        ...newXYValue,
      },
    });
  }
  handleDragLineComplete(moreProps) {
    const { override } = this.state;
    const { regressions } = this.props;
    if (isDefined(override)) {
      const newRegressions = regressions.map((each, idx) =>
        idx === override.index
          ? {
              ...each,
              start: [override.x1Value],
              end: [override.x2Value],
              selected: true,
            }
          : {
              ...each,
              selected: false,
            },
      );
      this.setState(
        {
          override: null,
        },
        () => {
          this.props.onComplete(newRegressions, moreProps);
        },
      );
    }
  }
  handleDrawLine(xyValue) {
    const { current } = this.state;

    if (isDefined(current) && isDefined(current.start)) {
      this.mouseMoved = true;
      this.setState({
        current: {
          start: current.start,
          end: xyValue,
        },
      });
    }
  }
  handleStart(xyValue) {
    const { current } = this.state;

    if (isNotDefined(current) || isNotDefined(current.start)) {
      this.mouseMoved = false;

      this.setState(
        {
          current: {
            start: xyValue,
            end: null,
          },
        },
        () => {
          this.props.onStart();
        },
      );
    }
  }
  handleEnd(xyValue, moreProps, e) {
    const { current } = this.state;
    const { appearance, regressions, type } = this.props;

    if (this.mouseMoved && isDefined(current) && isDefined(current.start)) {
      const newRegressions = [
        ...regressions.map((d) => ({ ...d, selected: false })),
        {
          start: current.start,
          end: xyValue,
          selected: true,
          appearance,
          type,
        },
      ];
      this.setState(
        {
          current: null,
        },
        () => {
          this.props.onComplete(newRegressions, moreProps, e);
        },
      );
    }
  }
  render() {
    const { appearance } = this.props;
    const { enabled, currentPositionRadius, currentPositionStroke } =
      this.props;
    const { currentPositionstrokeOpacity, currentPositionStrokeWidth } =
      this.props;
    const { hoverText, regressions, snap, snapTo, shouldDisableSnap } =
      this.props;
    const { current, override } = this.state;

    // Use registry format appearance directly
    const eachDefaultAppearance = appearance;

    const hoverTextDefault = {
      ...RegressionLine.defaultProps.hoverText,
      ...hoverText,
    };

    // Preview regression line while drawing
    const tempLine =
      isDefined(current) && isDefined(current.end) ? (
        <EachRegressionLine
          interactive={false}
          x1Value={current.start[0]}
          x2Value={current.end[0]}
          appearance={eachDefaultAppearance}
          hoverText={{
            enable: false,
          }}
          selected={false}
        />
      ) : null;

    return (
      <g>
        {regressions.map((each, idx) => {
          // Merge main appearance with individual regression appearance (shallow merge)
          const eachAppearance = isDefined(each.appearance)
            ? { ...eachDefaultAppearance, ...each.appearance }
            : eachDefaultAppearance;

          const eachHoverText = isDefined(each.hoverText)
            ? { ...hoverTextDefault, ...each.hoverText }
            : hoverTextDefault;

          return (
            <EachRegressionLine
              key={idx}
              ref={this.saveNodeType(idx)}
              index={idx}
              selected={each.selected}
              x1Value={getValueFromOverride(
                override,
                idx,
                "x1Value",
                each.start[0],
              )}
              x2Value={getValueFromOverride(
                override,
                idx,
                "x2Value",
                each.end[0],
              )}
              appearance={eachAppearance}
              snapTo={snapTo}
              hoverText={eachHoverText}
              onDrag={this.handleDragLine}
              onDragComplete={this.handleDragLineComplete}
            />
          );
        })}
        {tempLine}
        <MouseLocationIndicator
          enabled={enabled}
          snap={snap}
          shouldDisableSnap={shouldDisableSnap}
          snapTo={snapTo}
          r={currentPositionRadius}
          stroke={currentPositionStroke}
          strokeOpacity={currentPositionstrokeOpacity}
          strokeWidth={currentPositionStrokeWidth}
          onMouseDown={this.handleStart}
          onClick={this.handleEnd}
          onMouseMove={this.handleDrawLine}
          data-evo-id="interactive-regression-line"
        />
      </g>
    );
  }
}

RegressionLine.propTypes = {
  snap: PropTypes.bool.isRequired,
  enabled: PropTypes.bool.isRequired,
  snapTo: PropTypes.func,
  shouldDisableSnap: PropTypes.func.isRequired,

  onStart: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onSelect: PropTypes.func,

  currentPositionStroke: PropTypes.string,
  currentPositionStrokeWidth: PropTypes.number,
  currentPositionstrokeOpacity: PropTypes.number,
  currentPositionRadius: PropTypes.number,

  hoverText: PropTypes.object.isRequired,

  regressions: PropTypes.array.isRequired,
  type: PropTypes.string,

  appearance: PropTypes.shape({
    stroke: PropTypes.shape({
      color: PropTypes.string,
      width: PropTypes.number,
      opacity: PropTypes.number,
      dash: PropTypes.string,
    }),
  }).isRequired,
};

RegressionLine.defaultProps = {
  type: "RegressionLine",

  onStart: noop,
  onComplete: noop,
  onSelect: noop,

  currentPositionStroke: "#000000",
  currentPositionstrokeOpacity: 1,
  currentPositionStrokeWidth: 3,
  currentPositionRadius: 0,

  shouldDisableSnap: (e) => e.button === 2 || e.shiftKey,
  snap: false,

  hoverText: {
    ...HoverTextNearMouse.defaultProps,
    enable: false,
    bgHeight: "auto",
    bgWidth: "auto",
    text: "Clique para selecionar",
    selectedText: "",
  },

  regressions: [],

  appearance: {
    stroke: {
      color: "#2196F3",
      width: 2,
      opacity: 1,
      dash: "Solid",
    },
  },
};

export default RegressionLine;
